import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense } from 'react';
import RouteLoadingFallback from '../routing/RouteLoadingFallback';
import { prefetchCommonRoutes } from '../../routes/prefetchRoutes';
import { prefetchAgendaClinicSettings } from '../../utils/agendaClinicSettingsPrefetch';
import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  Divider,
  Avatar,
  useTheme,
  SwipeableDrawer,
  Tooltip,
  Badge as MuiUnreadBadge,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  WhatsApp as WhatsAppIcon,
  QrCode as QrCodeIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Forum as TeamChatIcon,
  Send as SendIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Business as BusinessIcon,
  ViewModule as AdSetIcon,
  Article as AdsIcon,
  PlaylistAdd as BulkIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as RevenueIcon,
  TrendingDown as ExpenseIcon,
  ViewKanban as KanbanIcon,
  IntegrationInstructions as IntegrationIcon,
  SupportAgent as SupportAgentIcon,
  CloudQueue as CloudIcon,
  CalendarToday as CalendarTodayIcon,
  LocalHospital as ClinicIcon,
  MedicalServices as MedicalServicesIcon,
  EventNote as AppointmentIcon,
  Healing as ProceduresIcon,
  CardMembership as PatientPlanIcon,
  Badge as ProfessionalsIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Description as PrescriptionIcon,
  Science as ExamOrderIcon,
  Biotech as LabIcon,
  VerifiedUser as CertificateIcon,
  Shortcut as ReferralIcon,
  ReceiptLong as ReceiptsIcon,
  HealthAndSafety as InsuranceIcon,
  Percent as CommissionIcon,
  EventSeat as WaitingRoomIcon,
  PhotoLibrary as GalleryIcon,
  AccountTree as TreatmentPlanIcon,
  AccountTree as PlanningIcon,
  GridOn as OdontogramIcon,
  Gesture as SignatureIcon,
  HistoryEdu as ContractTemplateIcon,
  ShowChart as PeriodontalIcon,
  Healing as DentalTreatmentIcon,
  Analytics as ReportsIcon,
  NotificationsActive as ReminderIcon,
  Timeline as EvolutionIcon,
  Article as MedicalReportIcon,
  PointOfSale as PointOfSaleIcon,
  Inventory as InventoryModIcon,
  ShoppingCart as ProductIcon,
  Build as MaterialMenuIcon,
  LocalPharmacy as MedicationIcon,
  HealthAndSafety as InsuranceMedIcon,
  ViewModule as KitMenuIcon,
  Place as LocationMenuIcon,
  Factory as ManufacturerIcon,
  SwapVert as MovementIcon,
  Category as CategoryIcon,
  Tune as AttributesIcon,
  Layers as ProductLinesIcon,
  Policy as AuditIcon,
  AccountBalanceWallet as WalletIcon,
  SwapHoriz as RepasseIcon,
  RequestQuote as InvoiceIcon,
  Store as StoreIcon,
  CallReceived as CheckInIcon,
  CallMade as CheckOutIcon,
  Payments as PaymentsIcon,
  Repeat as RecurringIcon,
  TrendingUp as TrendingUpIcon,
  TripOriginOutlined as TripOriginOutlinedIcon,
  EventAvailable as DoctoraliaIcon,
  MenuBook as TutoriaisIcon,
  Cake as CakeIcon,
  Assignment as FormsIcon,
  Assignment as TaskIcon,
  LocalShipping as ShippingIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { usePWA } from '../../hooks/usePWA';
import LogoutButton from '../LogoutButton';
import NotificationPermissionDialog from '../NotificationPermissionDialog';
import useNotifications from '../../hooks/useNotifications';
import PaymentNotificationBanner from '../PaymentNotificationBanner';
import PlatformBillingBanner from '../PlatformBillingBanner';
import PlatformContractGate from '../platform/PlatformContractGate';
import { usePlatformBilling } from '../../hooks/usePlatformBilling';
import { usePaymentNotification } from '../../contexts/PaymentNotificationContext';
import { inboxService, InboxMenuItem } from '../../services/inboxService';
import { inboxConversationCache } from '../../services/inboxConversationCache';
import { useAuth } from '../../contexts/AuthContext';
import GlobalSystemFinder from './GlobalSystemFinder';
import {
  SystemRole,
  getRoleLabel,
  ROLE_COLORS,
  professionalCanAccessDynamicInboxFromUser,
  canAccessWhatsAppConfig,
  userCanAccessInventoryModule,
} from '../../config/permissions';
import { resolveUserAvatarUrl } from '../../constants/userAvatars';
import { getWebSocketUrl, config as envConfig } from '../../config/environment';
import { isKlinviaNativeApp } from '../../utils/nativeApp';
import { usePlatformSupportHubRealtime } from '../../hooks/usePlatformSupportHubRealtime';
import UnitSwitcher from './UnitSwitcher';
import ImpersonationBanner from './ImpersonationBanner';
import {
  SIDEBAR_THEME,
  sidebarDesktopPaperSx,
  sidebarDrawerInnerSx,
  sidebarFooterSx,
  sidebarMenuButtonSx,
  sidebarMenuIconSx,
  sidebarMenuTextSx,
  sidebarSubmenuLabelRowSx,
  sidebarSubmenuLabelTextSx,
  sidebarSubmenuListSx,
  sidebarSubmenuUnreadPillSx,
  sidebarCollapsedUnreadBadgeSx,
  sidebarUnreadPillSx,
  sidebarUserRowSx,
  type SidebarUnreadBadgeTone,
} from './sidebarTheme';
import { useWaitingRoomMenuAlerts } from '../../hooks/useWaitingRoomMenuAlerts';
import TeamChatDock from '../../pages/TeamChat/floating/TeamChatDock';
import VirtualAssistantWidget from '../support/VirtualAssistantWidget';
import SupportCenterIcon from '../support/SupportCenterIcon';
import { SUPPORT_CENTER_DISPLAY_NAME } from '../../services/assistantCentral';
import { BRAND_LOGO_MENU_COLLAPSED, BRAND_LOGO_MENU_EXPANDED } from '../../config/brandAssets';
import { useClinicMedicalSpecialty } from '../../hooks/useClinicMedicalSpecialty';
import { usePlansEnabled } from '../../hooks/usePlansEnabled';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import MobileDesktopModeBanner from '../MobileDesktopModeBanner';
import BrowserZoomOnboarding from '../onboarding/BrowserZoomOnboarding';
import UserProfileDialog from '../settings/UserProfileDialog';
import {
  markBrowserZoomOnboardingSeen,
  shouldShowBrowserZoomOnboarding,
  clearFreshLoginZoomFlag,
} from '../../utils/browserZoomOnboarding';

const drawerWidth = 240;
const drawerWidthCollapsed = 88;
/** Deslocamento horizontal do paper do menu (deve entrar na largura reservada no flex) */
const drawerMarginLeft = 16;
/** Folga entre menu e área principal */
const mainContentGap = 16;
/** Margem vertical mínima do menu lateral (topo e rodapé) */
const drawerMarginVertical = 6;
/** Recolhe o menu desktop após o ponteiro sair (hover flyout). */
const SIDEBAR_COLLAPSE_DELAY_MS = 200;
const desktopSidebarReservedWidth = drawerWidthCollapsed + drawerMarginLeft;

const MainLayout: React.FC = () => {
  const { isOdontologia, isFisioterapia } = useClinicMedicalSpecialty();
  const { plansEnabled } = usePlansEnabled();
  const { notification } = usePaymentNotification();
  const bannerHeight = notification.active ? 32 : 0;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const sidebarCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Impede recolher enquanto um menu do rodapé (ex.: troca de unidade) está aberto fora do paper. */
  const sidebarDropdownLockRef = useRef(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [whatsappAutoOpen, setWhatsappAutoOpen] = useState(false);
  const [attendantsOpen, setAttendantsOpen] = useState(false);
  const [clinicOpen, setClinicOpen] = useState(false);
  const [financeiroOpen, setFinanceiroOpen] = useState(false);
  const [estoqueOpen, setEstoqueOpen] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [showZoomOnboarding, setShowZoomOnboarding] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [dynamicInboxes, setDynamicInboxes] = useState<InboxMenuItem[]>([]);
  const [inboxesLoaded, setInboxesLoaded] = useState(false);
  const [inboxUnreadByChannel, setInboxUnreadByChannel] = useState<Record<string, number>>({});
  const [teamChatUnread, setTeamChatUnread] = useState(0);
  const [waitingRoomMenuCount, setWaitingRoomMenuCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isMobileMd: isMobile, desktopModeOnPhone } = useDeviceLayout();
  const { isStandalone } = usePWA();
  const {
    shouldShowPermissionDialog,
    markPermissionDialogShown,
    isSupported: notificationSupported
  } = useNotifications();

  // Carregar inboxes dinâmicos da API; recarrega ao trocar de unidade
  useEffect(() => {
    const loadDynamicInboxes = async (forceRefresh = false) => {
      try {
        const menuData = await inboxService.getMenuInboxes(forceRefresh);
        const all = (menuData.all || []).filter((i) => i.purpose !== 'platform_support');
        setDynamicInboxes(all);
        setInboxesLoaded(true);
        try {
          const unread = await inboxService.getUnreadCounts();
          setInboxUnreadByChannel(unread.byChannel || {});
        } catch {
          /* não bloqueia menu */
        }
      } catch (error) {
        console.error('Erro ao carregar inboxes dinâmicos:', error);
        setDynamicInboxes([
          {
            id: 'default-onboarding',
            text: 'WhatsApp 1',
            path: '/inbox/whatsapp',
            type: 'onboarding',
            provider: 'evolution_api',
            isDefault: true,
            channelType: 'whatsapp',
            icon: 'whatsapp',
            badge: null
          },
        ]);
        setInboxesLoaded(true);
      }
    };

    loadDynamicInboxes();

    // Quando o usuário trocar de unidade, invalidar o cache e recarregar
    const handleUnitChanged = () => {
      inboxService.invalidateCache();
      loadDynamicInboxes(true);
    };
    window.addEventListener('unit:changed', handleUnitChanged);
    return () => window.removeEventListener('unit:changed', handleUnitChanged);
  }, []);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((open) => !open);
  }, []);

  const clearSidebarCollapseTimer = useCallback(() => {
    if (sidebarCollapseTimerRef.current != null) {
      clearTimeout(sidebarCollapseTimerRef.current);
      sidebarCollapseTimerRef.current = null;
    }
  }, []);

  const expandSidebar = useCallback(() => {
    if (isMobile) return;
    clearSidebarCollapseTimer();
    setDrawerOpen(true);
  }, [isMobile, clearSidebarCollapseTimer]);

  const scheduleSidebarCollapse = useCallback(() => {
    if (isMobile || sidebarDropdownLockRef.current) return;
    clearSidebarCollapseTimer();
    sidebarCollapseTimerRef.current = setTimeout(() => {
      setDrawerOpen(false);
      sidebarCollapseTimerRef.current = null;
    }, SIDEBAR_COLLAPSE_DELAY_MS);
  }, [isMobile, clearSidebarCollapseTimer]);

  const handleSidebarFooterDropdownOpenChange = useCallback(
    (open: boolean) => {
      sidebarDropdownLockRef.current = open;
      if (open) {
        clearSidebarCollapseTimer();
        setDrawerOpen(true);
      } else {
        scheduleSidebarCollapse();
      }
    },
    [clearSidebarCollapseTimer, scheduleSidebarCollapse],
  );

  useEffect(() => () => clearSidebarCollapseTimer(), [clearSidebarCollapseTimer]);

  const menuExpanded = isMobile || drawerOpen;

  // Auto-close mobile drawer when route changes
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Auto-close submenus on mobile when drawer closes
  useEffect(() => {
    if (!mobileOpen && isMobile) {
      setInboxOpen(false);
      setCampaignsOpen(false);
      setWhatsappAutoOpen(false);
      setAttendantsOpen(false);
      setClinicOpen(false);
      setFinanceiroOpen(false);
      setEstoqueOpen(false);
    }
  }, [mobileOpen, isMobile]);

  // Auto-open submenus when on their routes
  useEffect(() => {
    if (location.pathname.startsWith('/attendants')) {
      setAttendantsOpen(true);
    }
    if (location.pathname.startsWith('/clinic') && !location.pathname.startsWith('/clinic/reports')) {
      const doctoraliaOnlyTopLevel = location.pathname.startsWith('/clinic/doctoralia') && !isOdontologia;
      if (!doctoraliaOnlyTopLevel) {
        setClinicOpen(true);
      }
    }
    if (location.pathname.startsWith('/financial')) {
      setFinanceiroOpen(true);
    }
    if (location.pathname.startsWith('/inventory')) {
      setEstoqueOpen(true);
    }
    if (
      location.pathname.startsWith('/whatsapp-auto')
      || location.pathname.startsWith('/whatsapp-cloud-api')
      || location.pathname.startsWith('/whatsapp-qr')
      || location.pathname.startsWith('/whatsapp-manual')
      || location.pathname.startsWith('/whatsapp-dashboard')
      || location.pathname.startsWith('/whatsapp-proxy')
    ) {
      setWhatsappAutoOpen(true);
    }
  }, [location.pathname, isOdontologia]);

  // Mostrar dialog de notificações quando necessário
  useEffect(() => {
    if (shouldShowPermissionDialog && isStandalone) {
      const timer = setTimeout(() => {
        setShowNotificationDialog(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [shouldShowPermissionDialog, isStandalone]);

  // Callbacks do dialog de notificações
  const handleNotificationPermissionGranted = () => {
    markPermissionDialogShown();
    console.log('Notificações ativadas com sucesso!');
  };

  const handleNotificationPermissionDenied = () => {
    markPermissionDialogShown();
    console.log('Notificações recusadas pelo usuário');
  };

  const handleCloseNotificationDialog = () => {
    setShowNotificationDialog(false);
  };

  // Submenu de inbox: somente inboxes dinâmicos da unidade ativa (isDefault=false).
  // Sem inbox dinâmico para a unidade → submenu vazio → "Central de Mensagens"
  // some do menu (filtro `submenu.length > 0` mais abaixo).
  const buildInboxSubmenu = () => {
    if (!inboxesLoaded) return [];

    const dynamicOnboarding = dynamicInboxes.filter(
      (inbox) => inbox.type === 'onboarding' && !inbox.isDefault,
    );

    return dynamicOnboarding.map((inbox) => {
      const cid = inbox.channelId;
      const unreadCount = cid ? inboxUnreadByChannel[cid] ?? 0 : 0;
      return {
        text: inbox.text,
        icon: <WhatsAppIcon />,
        path: inbox.path,
        badge: inbox.badge,
        type: inbox.type,
        channelId: cid,
        unreadCount,
      };
    });
  };

  // Obter dados do auth
  const { user: authUser, isAdmin: isUserAdmin, roleLabel: userRoleLabel, hasPermission } = useAuth();
  const userRole = authUser?.role || 'admin';
  const linkedProfessionalId = authUser?.professional_id || null;
  const showWaitingRoomTopLevel =
    Boolean(linkedProfessionalId) && hasPermission('/clinic/waiting-room');
  const canSeeWhatsAppConfig = canAccessWhatsAppConfig(userRole, authUser?.current_unit);

  useWaitingRoomMenuAlerts({
    enabled: showWaitingRoomTopLevel,
    professionalId: linkedProfessionalId,
    unitId: authUser?.current_unit?.id,
    onCountChange: setWaitingRoomMenuCount,
    suppressAlerts: location.pathname.startsWith('/clinic/waiting-room'),
  });

  const platformSupportHub = usePlatformSupportHubRealtime(
    Boolean(envConfig.platformAdminEnabled) && userRole === 'admin_master',
  );
  const platformSupportMenuUnread =
    platformSupportHub.whatsappUnreadTotal + platformSupportHub.ticketStats.open;

  useEffect(() => {
    const userId = authUser?.id;
    if (!userId || !shouldShowBrowserZoomOnboarding(userId)) return undefined;
    const timer = window.setTimeout(() => {
      setShowZoomOnboarding(true);
      // Evita reabrir ao recarregar a página na mesma sessão de login.
      clearFreshLoginZoomFlag();
    }, 700);
    return () => window.clearTimeout(timer);
  }, [authUser?.id]);

  // Revalida periodicamente: o desbloqueio após o pagamento não deve exigir F5.
  const platformBilling = usePlatformBilling({ poll: true });

  /** Admin/recepção usam `/inbox`; profissional com inbox dinâmico só tem `/inbox/<slug>`. */
  const canTrackInboxUnread = useMemo(() => {
    if (hasPermission('/inbox')) return true;
    return dynamicInboxes.some((inbox) => inbox.path && hasPermission(inbox.path));
  }, [dynamicInboxes, hasPermission]);

  const refreshInboxUnreadCounts = useCallback(async () => {
    if (!inboxesLoaded || !canTrackInboxUnread) return;
    try {
      const data = await inboxService.getUnreadCounts();
      setInboxUnreadByChannel(data.byChannel ?? {});
    } catch (e) {
      console.error('Erro ao carregar não lidas da Central:', e);
    }
  }, [inboxesLoaded, canTrackInboxUnread]);

  useEffect(() => {
    void refreshInboxUnreadCounts();
  }, [refreshInboxUnreadCounts]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.hidden) return;
      void refreshInboxUnreadCounts();
    }, 35000);
    return () => window.clearInterval(id);
  }, [refreshInboxUnreadCounts]);

  useEffect(() => {
    prefetchCommonRoutes();
  }, []);

  useEffect(() => {
    const unitId = authUser?.current_unit?.id;
    if (unitId) prefetchAgendaClinicSettings(unitId);
  }, [authUser?.current_unit?.id]);

  useEffect(() => {
    if (location.pathname.startsWith('/inbox')) {
      void refreshInboxUnreadCounts();
    }
  }, [location.pathname, refreshInboxUnreadCounts]);

  const refreshInboxUnreadCountsRef = useRef(refreshInboxUnreadCounts);
  refreshInboxUnreadCountsRef.current = refreshInboxUnreadCounts;

  const inboxUnreadDebounceRef = useRef<number | null>(null);
  const inboxLayoutSocketRef = useRef<Socket | null>(null);

  const scheduleInboxUnreadRefreshFromRealtime = useCallback(() => {
    if (inboxUnreadDebounceRef.current) {
      window.clearTimeout(inboxUnreadDebounceRef.current);
    }
    inboxUnreadDebounceRef.current = window.setTimeout(() => {
      inboxUnreadDebounceRef.current = null;
      void refreshInboxUnreadCountsRef.current();
    }, 300);
  }, []);

  /** Atualiza contadores do menu quando o backend emite mensagem/conversa no canal da unidade. */
  useEffect(() => {
    if (!canTrackInboxUnread) {
      if (inboxLayoutSocketRef.current) {
        inboxLayoutSocketRef.current.removeAllListeners();
        inboxLayoutSocketRef.current.disconnect();
        inboxLayoutSocketRef.current = null;
      }
      return;
    }

    const wsUrl = getWebSocketUrl();
    if (!wsUrl?.trim()) {
      return;
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      return;
    }

    const channelIds = dynamicInboxes
      .map((inbox) => inbox.channelId)
      .filter((id): id is string => Boolean(id));

    const socket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      auth: { token },
    });

    inboxLayoutSocketRef.current = socket;

    const subscribeInboxChannels = () => {
      channelIds.forEach((channelId) => {
        socket.emit('subscribe_channel', channelId);
      });
      // Mensagens recebidas enquanto o socket estava fora não geram evento: reconsultar.
      scheduleInboxUnreadRefreshFromRealtime();
    };

    const bump = (payload: any) => {
      const envelope = payload?.update ? payload : null;
      const message = payload?.message || payload;
      const payloadChannelId =
        message?.channel_id
        || message?.channel?.id
        || envelope?.update?.channel_id
        || envelope?.channel_id
        || null;
      // Com canais ainda não carregados no menu, não descartar eventos (evita badge zerado).
      if (
        channelIds.length > 0
        && payloadChannelId
        && !channelIds.includes(String(payloadChannelId))
      ) {
        return;
      }
      scheduleInboxUnreadRefreshFromRealtime();
      inboxConversationCache.handleRealtimeEvent(payload);
    };

    socket.on('connect', subscribeInboxChannels);
    socket.on('reconnect', subscribeInboxChannels);
    socket.on('new_message', bump);
    socket.on('conversation_updated', bump);

    return () => {
      socket.off('connect', subscribeInboxChannels);
      socket.off('reconnect', subscribeInboxChannels);
      socket.off('new_message', bump);
      socket.off('conversation_updated', bump);
      socket.disconnect();
      if (inboxLayoutSocketRef.current === socket) {
        inboxLayoutSocketRef.current = null;
      }
      if (inboxUnreadDebounceRef.current) {
        window.clearTimeout(inboxUnreadDebounceRef.current);
        inboxUnreadDebounceRef.current = null;
      }
    };
  }, [scheduleInboxUnreadRefreshFromRealtime, canTrackInboxUnread, dynamicInboxes]);

  useEffect(() => {
    const onInboxMenuUnreadRefresh = () => scheduleInboxUnreadRefreshFromRealtime();
    const onVisible = () => {
      if (document.visibilityState === 'visible') scheduleInboxUnreadRefreshFromRealtime();
    };
    window.addEventListener('inbox:menu-unread-refresh', onInboxMenuUnreadRefresh as EventListener);
    window.addEventListener('focus', onInboxMenuUnreadRefresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('inbox:menu-unread-refresh', onInboxMenuUnreadRefresh as EventListener);
      window.removeEventListener('focus', onInboxMenuUnreadRefresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [scheduleInboxUnreadRefreshFromRealtime]);

  // Filtrar itens de menu baseado no perfil do usuário
  const filterByRole = (items: any[]) => {
    return items.filter((item: any) => {
      if (typeof item.showWhen === 'function' && !item.showWhen()) return false;
      if (item.path) return hasPermission(item.path);
      if (item.roles) return item.roles.includes(userRole);
      return true;
    });
  };

  const cashRegisterPath = '/financial/cash-register';
  const showCashRegisterShortcut = hasPermission(cashRegisterPath);
  const isCashRegisterActive =
    location.pathname === cashRegisterPath ||
    location.pathname.startsWith(`${cashRegisterPath}/`);

  const clinicSettingsPath = '/clinic/settings';
  const showClinicSettingsShortcut = hasPermission(clinicSettingsPath);
  const isClinicSettingsActive =
    location.pathname === clinicSettingsPath ||
    location.pathname.startsWith(`${clinicSettingsPath}/`);

  const allMenuItems = [
    { text: 'Agenda', icon: <CalendarTodayIcon />, path: '/agenda', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'] as SystemRole[] },
    ...(showWaitingRoomTopLevel
      ? [{
          text: 'Sala de Espera',
          icon: <WaitingRoomIcon />,
          path: '/clinic/waiting-room',
          unreadCount: waitingRoomMenuCount,
          unreadBadgeTone: 'warning' as SidebarUnreadBadgeTone,
          showWhen: () => showWaitingRoomTopLevel,
        }]
      : []),
    { text: 'Auditoria diária', icon: <AuditIcon />, path: '/clinic/daily-audit', roles: ['admin', 'admin_master', 'receptionist', 'financial'] as SystemRole[] },
    {
      text: 'Central de Mensagens',
      icon: <MessageIcon />,
      hasSubmenu: true,
      isOpen: inboxOpen,
      toggle: () => setInboxOpen(!inboxOpen),
      submenu: buildInboxSubmenu(),
      roles: ['admin', 'admin_master', 'receptionist'] as SystemRole[],
    },
    ...(envConfig.platformAdminEnabled
      ? [
          {
            text: 'Inbox de suporte',
            icon: <SupportAgentIcon />,
            path: '/platform-support',
            roles: ['admin_master'] as SystemRole[],
            unreadCount: platformSupportMenuUnread,
          },
        ]
      : []),
    {
      text: 'Chat da Equipe',
      icon: <TeamChatIcon />,
      path: '/team-chat',
      unreadCount: teamChatUnread,
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'] as SystemRole[],
    },
    { text: SUPPORT_CENTER_DISPLAY_NAME, icon: <SupportCenterIcon />, path: '/central-ia', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'] as SystemRole[] },
    { text: 'Central de Atendimento IA', icon: <SupportAgentIcon />, path: '/support', roles: ['admin', 'admin_master'] as SystemRole[] },
    ...(!isOdontologia ? [{
      text: 'Doctoralia',
      icon: <DoctoraliaIcon />,
      path: '/clinic/doctoralia',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[],
    }] : []),
    { text: 'CRM', icon: <KanbanIcon />, path: '/crm', roles: ['admin', 'admin_master', 'receptionist'] as SystemRole[] },
    { text: 'Pacientes', icon: <PeopleIcon />, path: '/clinic/patients', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[] },
    { text: 'Evoluções', icon: <EvolutionIcon />, path: '/clinic/evolutions', roles: ['admin', 'admin_master', 'professional', 'assistant'] as SystemRole[] },
    ...(isFisioterapia ? [{
      text: 'Avaliações',
      icon: <FormsIcon />,
      path: '/clinic/physiotherapy-assessments',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[],
    }] : []),
    ...(!isFisioterapia && !isOdontologia ? [{
      text: 'Relatórios Médicos',
      icon: <MedicalReportIcon />,
      path: '/clinic/medical-reports',
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[],
    }] : []),
    ...(!isFisioterapia ? [{
      text: 'Receituários',
      icon: <PrescriptionIcon />,
      path: '/clinic/prescriptions',
      roles: ['admin', 'admin_master', 'professional', 'receptionist'] as SystemRole[],
    }] : []),
    { text: 'Orçamentos', icon: <ReceiptIcon />, path: '/clinic/budgets', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial'] as SystemRole[] },
    ...(isOdontologia ? [{
      text: 'Planejamentos',
      icon: <PlanningIcon />,
      path: '/clinic/plannings',
      roles: ['admin', 'admin_master', 'professional', 'receptionist'] as SystemRole[],
    }] : []),
    {
      text: 'Clínica',
      icon: <MedicalServicesIcon />,
      hasSubmenu: true,
      isOpen: clinicOpen,
      toggle: () => setClinicOpen(!clinicOpen),
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[],
      submenu: filterByRole([
        { text: 'Dashboard Conversas', icon: <DashboardIcon />, path: '/dashboard', roles: ['admin', 'admin_master'] as SystemRole[] },
        {
          text: 'Sala de Espera',
          icon: <WaitingRoomIcon />,
          path: '/clinic/waiting-room',
          roles: ['admin', 'admin_master', 'receptionist', 'assistant', 'professional'] as SystemRole[],
          showWhen: () => !showWaitingRoomTopLevel,
        },
        { text: 'Procedimentos', icon: <ProceduresIcon />, path: '/clinic/procedures' },
        ...(plansEnabled ? [{
          text: 'Planos',
          icon: <PatientPlanIcon />,
          path: '/clinic/patient-plans',
          roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial'] as SystemRole[],
        }] : []),
        { text: 'Origens de pacientes', icon: <TripOriginOutlinedIcon sx={{ fontSize: 22 }} />, path: '/clinic/patient-sources' },
        { text: 'Profissionais', icon: <ProfessionalsIcon />, path: '/clinic/professionals' },
        ...(!isFisioterapia ? [{
          text: 'Receituários',
          icon: <PrescriptionIcon />,
          path: '/clinic/prescriptions',
          roles: ['admin', 'admin_master', 'professional', 'receptionist'] as SystemRole[],
        }] : []),
        ...(isFisioterapia ? [{ text: 'Avaliações', icon: <FormsIcon />, path: '/clinic/physiotherapy-assessments' }] : []),
        ...(!isFisioterapia ? [
          { text: 'Pedidos de Exames', icon: <ExamOrderIcon />, path: '/clinic/exam-orders' },
          { text: 'Laboratório', icon: <LabIcon />, path: '/clinic/laboratory' },
          { text: 'Atestados', icon: <CertificateIcon />, path: '/clinic/certificates' },
        ] : []),
        ...(!isFisioterapia && !isOdontologia ? [{ text: 'Relatórios Médicos', icon: <MedicalReportIcon />, path: '/clinic/medical-reports' }] : []),
        { text: 'Encaminhamentos', icon: <ReferralIcon />, path: '/clinic/referrals' },
        {
          text: 'Assinatura de Documentos',
          icon: <SignatureIcon />,
          path: '/clinic/document-signing',
          roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[],
        },
        {
          text: 'Modelos de Contrato',
          icon: <ContractTemplateIcon />,
          path: '/clinic/contract-templates',
          roles: ['admin', 'admin_master'] as SystemRole[],
        },
        { text: 'Convênios / TISS', icon: <InsuranceIcon />, path: '/clinic/insurance' },
        { text: 'Galeria Clínica', icon: <GalleryIcon />, path: '/clinic/image-gallery' },
        ...(isOdontologia ? [
          {
            text: 'Doctoralia',
            icon: <DoctoraliaIcon />,
            path: '/clinic/doctoralia',
            roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[],
          },
          { text: 'Relatórios Médicos', icon: <MedicalReportIcon />, path: '/clinic/medical-reports' },
          { text: 'Odontograma', icon: <OdontogramIcon />, path: '/clinic/odontogram', roles: ['admin', 'admin_master', 'professional', 'assistant'] as SystemRole[] },
          { text: 'Periodontograma', icon: <PeriodontalIcon />, path: '/clinic/periodontal-charts', roles: ['admin', 'admin_master', 'professional', 'assistant'] as SystemRole[] },
          { text: 'Tratamentos Odonto', icon: <DentalTreatmentIcon />, path: '/clinic/dental-treatments', roles: ['admin', 'admin_master', 'professional', 'assistant'] as SystemRole[] },
        ] : []),
        ...(isOdontologia ? [{ text: 'Templates Planejamento', icon: <PlanningIcon />, path: '/clinic/planning-templates', roles: ['admin', 'admin_master'] as SystemRole[] }] : []),
        { text: 'Confirmação Auto', icon: <ReminderIcon />, path: '/clinic/reminder-settings' },
        { text: 'Aniversariantes', icon: <CakeIcon />, path: '/clinic/birthdays', roles: ['admin', 'admin_master', 'receptionist'] as SystemRole[] },
        { text: 'Config. Clínica', icon: <SettingsIcon />, path: '/clinic/settings' },
      ])
    },
    {
      text: 'Financeiro',
      icon: <AccountBalanceIcon />,
      hasSubmenu: true,
      isOpen: financeiroOpen,
      toggle: () => setFinanceiroOpen(!financeiroOpen),
      roles: ['admin', 'admin_master', 'financial', 'receptionist'] as SystemRole[],
      submenu: filterByRole([
        { text: 'Resumo', icon: <DashboardIcon />, path: '/financial/dashboard' },
        { text: 'DRE Gerencial', icon: <AssessmentIcon />, path: '/financial/dre' },
        { text: 'Plano de Contas', icon: <TreatmentPlanIcon />, path: '/financial/chart-of-accounts' },
        { text: 'Contas a Pagar', icon: <ExpenseIcon />, path: '/financial/accounts-payable' },
        { text: 'NF-e Recebidas', icon: <InvoiceIcon />, path: '/financial/nfe-received' },
        { text: 'Despesas Fixas', icon: <RecurringIcon />, path: '/financial/accounts-payable?tab=fixed' },
        { text: 'Contas a Receber', icon: <TrendingUpIcon />, path: '/financial/accounts-receivable' },
        { text: 'Recebíveis de Cartão', icon: <PaymentsIcon />, path: '/financial/card-settlements' },
        { text: 'Planos de Saúde', icon: <InsuranceIcon />, path: '/financial/insurance-receivables' },
        { text: 'Receitas Fixas', icon: <RevenueIcon />, path: '/financial/accounts-receivable?tab=fixed' },
        { text: 'Caixa', icon: <PointOfSaleIcon />, path: '/financial/cash-register' },
        { text: 'Auditoria', icon: <AuditIcon />, path: '/financial/audit' },
        { text: 'Extratos', icon: <WalletIcon />, path: '/financial/bank-statements' },
        { text: 'Repasses', icon: <RepasseIcon />, path: '/financial/repasses' },
        { text: 'Regras de Comissão', icon: <RepasseIcon />, path: '/financial/commission-rules' },
        { text: 'Transações TEF', icon: <PaymentsIcon />, path: '/financial/tef' },
        { text: 'Notas Fiscais', icon: <InvoiceIcon />, path: '/financial/nfse' },
        { text: 'Ativar Emissão NFe', icon: <BusinessIcon />, path: '/financial/nfse-companies', roles: ['admin', 'admin_master', 'financial', 'receptionist'] as SystemRole[] },
        { text: 'Recibos', icon: <ReceiptsIcon />, path: '/financial/receipts' },
        { text: 'Cheques Recebidos', icon: <CheckInIcon />, path: '/financial/checks-received' },
        { text: 'Cheques Emitidos', icon: <CheckOutIcon />, path: '/financial/checks-issued' },
      ])
    },
    {
      text: 'Estoque',
      icon: <InventoryModIcon />,
      hasSubmenu: true,
      isOpen: estoqueOpen,
      toggle: () => setEstoqueOpen(!estoqueOpen),
      showWhen: () => Boolean(authUser && userCanAccessInventoryModule(authUser)),
      submenu: filterByRole([
        { text: 'Geral', icon: <InventoryModIcon />, path: '/inventory/items' },
        { text: 'Produto', icon: <ProductIcon />, path: '/inventory/items?type=produto' },
        { text: 'Material', icon: <MaterialMenuIcon />, path: '/inventory/items?type=material' },
        ...(!isFisioterapia
          ? [
            { text: 'Medicamento', icon: <MedicationIcon />, path: '/inventory/items?type=medicamento' },
            { text: 'Med. por Convênio', icon: <InsuranceMedIcon />, path: '/inventory/insurance-medications' },
          ]
          : []),
        { text: 'Categorias', icon: <CategoryIcon />, path: '/inventory/categories' },
        { text: 'Campos de Produto', icon: <AttributesIcon />, path: '/inventory/attributes' },
        { text: 'Linhas de Produto', icon: <ProductLinesIcon />, path: '/inventory/product-lines' },
        { text: 'Localizações', icon: <LocationMenuIcon />, path: '/inventory/locations' },
        { text: 'Fabricantes', icon: <ManufacturerIcon />, path: '/inventory/manufacturers' },
        { text: 'Fornecedores', icon: <StoreIcon />, path: '/inventory/suppliers' },
        { text: 'Kits', icon: <KitMenuIcon />, path: '/inventory/kits' },
        { text: 'Movimentações', icon: <MovementIcon />, path: '/inventory/items?tab=movimentacoes' },
        { text: 'Consignado', icon: <StoreIcon />, path: '/inventory/consigned' },
        { text: 'Pendências', icon: <TaskIcon />, path: '/inventory/pending' },
        { text: 'Remessas / Transferências', icon: <ShippingIcon />, path: '/inventory/operations' },
        { text: 'Requisições', icon: <ReceiptIcon />, path: '/inventory/requisitions' },
      ])
    },
    { text: 'Relatórios', icon: <ReportsIcon />, path: '/clinic/reports', roles: ['admin', 'admin_master', 'financial'] as SystemRole[] },
    { text: 'Unidades', icon: <BusinessIcon />, path: '/admin/units', roles: ['admin', 'admin_master'] as SystemRole[] },
    { text: 'Gestão de Usuários', icon: <ProfessionalsIcon />, path: '/user-management', roles: ['admin_master'] as SystemRole[] },
    // Assinatura da Klinvia não aparece no app iOS (regra 3.1 da App Store).
    ...(isKlinviaNativeApp()
      ? []
      : [{ text: 'Plano da plataforma', icon: <PaymentsIcon />, path: '/platform-payments', roles: ['admin_master'] as SystemRole[] }]),
    // Painel central: existe apenas no build do deploy interno da Klinvia.
    ...(envConfig.platformAdminEnabled
      ? [
          { text: 'Clientes da plataforma', icon: <BusinessIcon />, path: '/platform-clients', roles: ['admin_master'] as SystemRole[] },
          { text: 'Gerenciamento de Clientes', icon: <GroupsIcon />, path: '/client-management', roles: ['admin_master'] as SystemRole[] },
          { text: 'Painel Klinvia', icon: <FormsIcon />, path: '/platform-crm-forms', roles: ['admin_master'] as SystemRole[] },
        ]
      : []),
    {
      text: 'Config WhatsApp',
      icon: <WhatsAppIcon />,
      hasSubmenu: true,
      isOpen: whatsappAutoOpen,
      toggle: () => setWhatsappAutoOpen(!whatsappAutoOpen),
      showWhen: () => canSeeWhatsAppConfig,
      submenu: filterByRole([
        { text: 'Cloud API', icon: <CloudIcon />, path: '/whatsapp-cloud-api' },
        { text: 'Gerenciar APIs', icon: <WhatsAppIcon />, path: '/whatsapp-auto' },
        { text: 'QR Code', icon: <QrCodeIcon />, path: '/whatsapp-qr' },
        { text: 'Envio Manual', icon: <SendIcon />, path: '/whatsapp-manual' },
      ]),
    },
    { text: 'Controle de Tarefas', icon: <KanbanIcon />, path: '/kanban', roles: ['admin', 'admin_master'] as SystemRole[] },
    {
      text: 'Campanhas',
      icon: <CampaignIcon />,
      hasSubmenu: true,
      isOpen: campaignsOpen,
      toggle: () => setCampaignsOpen(!campaignsOpen),
      roles: ['admin', 'admin_master'] as SystemRole[],
      submenu: [
        { text: 'SMS', icon: <EmailIcon />, path: '/campaigns/sms' },
        { text: 'Campanhas de Email', icon: <EmailIcon />, path: '/campaigns/email-campaigns' },
        { text: 'Templates de Email', icon: <EmailIcon />, path: '/campaigns/email-templates' },
      ]
    },
    {
      text: 'Atendentes',
      icon: <PeopleIcon />,
      hasSubmenu: true,
      isOpen: attendantsOpen,
      toggle: () => setAttendantsOpen(!attendantsOpen),
      roles: ['admin', 'admin_master'] as SystemRole[],
      submenu: [
        { text: 'Gerenciar Atendentes', icon: <PeopleIcon />, path: '/attendants' },
        { text: 'Dashboard', icon: <AssessmentIcon />, path: '/attendants/dashboard' },
      ]
    },
    { text: 'Tutoriais', icon: <TutoriaisIcon />, path: '/tutoriais', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[] },
  ];

  // Filtrar menu baseado no perfil do usuário logado
  const menuItems = allMenuItems.filter(item => {
    if (typeof item.showWhen === 'function') {
      return item.showWhen();
    }
    if (!item.roles) return true;
    if (item.text === 'Central de Mensagens') {
      return (
        item.roles.includes(userRole as SystemRole) ||
        (userRole === 'professional' && professionalCanAccessDynamicInboxFromUser(authUser))
      );
    }
    return item.roles.includes(userRole as SystemRole);
  }).filter(item => {
    // Se tem submenu, verificar se tem sub-itens visíveis
    if (item.hasSubmenu && item.submenu) {
      return item.submenu.length > 0;
    }
    return true;
  });

  const renderMenuItem = (item: any, index: number) => {
    // Para itens com submenu, verificar se algum subitem está ativo
    const isActive = item.hasSubmenu 
      ? item.submenu?.some((subItem: any) => {
          const subPathWithoutQuery = subItem.path?.split('?')[0] || subItem.path;
          const fullCurrentPath = `${location.pathname}${location.search}`;
          return fullCurrentPath === subItem.path ||
            location.pathname === subPathWithoutQuery ||
            (subPathWithoutQuery !== '/facebook-ads' && location.pathname.startsWith(subPathWithoutQuery));
        })
      : item.path === '/agenda'
        ? location.pathname === '/agenda'
        : location.pathname === item.path ||
          (item.path !== '/dashboard' && item.path !== '/crm' && location.pathname.startsWith(item.path));

    if (item.hasSubmenu) {
      const centralParentUnread =
        item.text === 'Central de Mensagens' && Array.isArray(item.submenu)
          ? item.submenu.reduce((acc: number, s: any) => acc + (Number(s.unreadCount) || 0), 0)
          : 0;

      return (
        <React.Fragment key={item.text}>
          <Tooltip title={!menuExpanded ? item.text : ''} placement="right" arrow>
            <ListItemButton
              onClick={() => {
                if (!menuExpanded) expandSidebar();
                item.toggle();
              }}
              sx={sidebarMenuButtonSx({ isActive, drawerOpen: menuExpanded })}
            >
              <ListItemIcon sx={sidebarMenuIconSx(isActive, menuExpanded)}>
                {item.text === 'Central de Mensagens' && !menuExpanded ? (
                  <MuiUnreadBadge
                    overlap="rectangular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    invisible={centralParentUnread === 0}
                    badgeContent={centralParentUnread > 99 ? '99+' : centralParentUnread}
                    color="error"
                    sx={sidebarCollapsedUnreadBadgeSx(centralParentUnread)}
                  >
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {item.icon}
                    </Box>
                  </MuiUnreadBadge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
          {menuExpanded && (
            <>
              <ListItemText primary={item.text} sx={sidebarMenuTextSx(isActive)} />
                  {item.text === 'Central de Mensagens' && centralParentUnread > 0 && (
                    <Box component="span" sx={sidebarUnreadPillSx()}>
                      {centralParentUnread > 99 ? '99+' : centralParentUnread}
                    </Box>
                  )}
                  {item.isOpen ? (
                    <ExpandLess sx={{ fontSize: 18, color: SIDEBAR_THEME.slateMid, flexShrink: 0 }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 18, color: SIDEBAR_THEME.slateMid, flexShrink: 0 }} />
                  )}
                </>
              )}
            </ListItemButton>
          </Tooltip>
          <Collapse 
            in={item.isOpen && menuExpanded} 
            timeout={300} 
            unmountOnExit 
            sx={{
              '& .MuiCollapse-wrapper': {
                display: 'block !important',
                width: '100% !important',
              },
              '& .MuiCollapse-wrapperInner': {
                display: 'block !important',
                width: '100% !important',
              },
              '& .MuiCollapse-root': {
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }
            }}
          >
            <List component="div" disablePadding sx={sidebarSubmenuListSx}>
              {item.submenu.map((subItem: any) => {
                const subPathWithoutQuery = subItem.path?.split('?')[0] || subItem.path;
                const fullCurrentPath = `${location.pathname}${location.search}`;
                const isSubActive = fullCurrentPath === subItem.path || location.pathname === subPathWithoutQuery;
                const subUnread = Number(subItem.unreadCount) || 0;
                return (
                  <ListItem key={subItem.text} disablePadding sx={{ 
                    width: '100%',
                    position: 'relative',
                    zIndex: 1,
                    overflow: 'visible',
                    minWidth: 0,
                  }}>
                    <Tooltip title={!menuExpanded ? subItem.text : subItem.text} placement="right" arrow>
                      <ListItemButton
                        onClick={() => {
                          if (!menuExpanded) expandSidebar();
                          navigate(subItem.path);
                        }}
                        sx={sidebarMenuButtonSx({ isActive: isSubActive, drawerOpen: menuExpanded, nested: true })}
                      >
                        <ListItemIcon sx={sidebarMenuIconSx(isSubActive, menuExpanded, '1.35rem')}>
                          {item.text === 'Central de Mensagens' && !menuExpanded && subUnread > 0 ? (
                            <MuiUnreadBadge
                              overlap="rectangular"
                              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                              badgeContent={subUnread > 99 ? '99+' : subUnread}
                              color="error"
                              sx={sidebarCollapsedUnreadBadgeSx(subUnread)}
                            >
                              <Box
                                component="span"
                                sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                {subItem.icon}
                              </Box>
                            </MuiUnreadBadge>
                          ) : (
                            subItem.icon
                          )}
                        </ListItemIcon>
                        {menuExpanded && (
                          <Box sx={sidebarSubmenuLabelRowSx}>
                            <Typography component="span" sx={sidebarSubmenuLabelTextSx(isSubActive)}>
                              {subItem.text}
                            </Typography>
                            {item.text === 'Central de Mensagens' && subUnread > 0 && (
                              <Box component="span" sx={sidebarSubmenuUnreadPillSx}>
                                {subUnread > 99 ? '99+' : subUnread}
                              </Box>
                            )}
                          </Box>
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    const flatUnread = Number(item.unreadCount) || 0;
    const unreadBadgeTone: SidebarUnreadBadgeTone =
      item.unreadBadgeTone === 'warning' ? 'warning' : 'error';
    const muiBadgeColor = unreadBadgeTone === 'warning' ? 'warning' : 'error';

    return (
      <Tooltip title={!menuExpanded ? item.text : ''} placement="right" arrow>
        <ListItemButton
          onClick={() => {
            if (!menuExpanded) expandSidebar();
            navigate(item.path);
          }}
          sx={sidebarMenuButtonSx({ isActive, drawerOpen: menuExpanded })}
        >
          <ListItemIcon sx={sidebarMenuIconSx(isActive, menuExpanded)}>
            {flatUnread > 0 && !menuExpanded ? (
              <MuiUnreadBadge
                overlap="rectangular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                badgeContent={flatUnread > 99 ? '99+' : flatUnread}
                color={muiBadgeColor}
                sx={sidebarCollapsedUnreadBadgeSx(flatUnread, unreadBadgeTone)}
              >
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {item.icon}
                </Box>
              </MuiUnreadBadge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
          {menuExpanded && (
            <>
              <ListItemText primary={item.text} sx={sidebarMenuTextSx(isActive)} />
              {flatUnread > 0 && (
                <Box component="span" sx={sidebarUnreadPillSx(unreadBadgeTone)}>
                  {flatUnread > 99 ? '99+' : flatUnread}
                </Box>
              )}
            </>
          )}
        </ListItemButton>
      </Tooltip>
    );
  };

  const drawer = (
    <Box
      className="flex flex-col w-full h-full relative z-10"
      sx={sidebarDrawerInnerSx}
      onMouseEnter={!isMobile ? expandSidebar : undefined}
      onMouseLeave={!isMobile ? scheduleSidebarCollapse : undefined}
    >
      {/* Logo completa quando expandido */}
      <Box
        className="flex items-center justify-center relative"
        sx={{
          position: 'relative',
          paddingTop: menuExpanded ? '20px' : 0,
          paddingBottom: menuExpanded ? '6px' : 0,
          minHeight: menuExpanded ? 'auto' : 0,
          overflow: 'hidden',
          zIndex: 2,
          transition: SIDEBAR_THEME.transition,
          display: menuExpanded ? 'flex' : 'none',
        }}
      >
        <Box
          sx={{
            opacity: menuExpanded ? 1 : 0,
            transform: menuExpanded ? 'scale(1)' : 'scale(0.92)',
            transition: SIDEBAR_THEME.transition,
            pointerEvents: menuExpanded ? 'auto' : 'none',
            position: menuExpanded ? 'relative' : 'absolute',
          }}
        >
          <img
            src={BRAND_LOGO_MENU_EXPANDED}
            alt="Logo"
            style={{ 
              height: 'auto',
              maxHeight: `${SIDEBAR_THEME.logoExpandedMaxHeight}px`,
              width: 'auto',
              maxWidth: `${SIDEBAR_THEME.logoExpandedMaxWidth}px`,
              objectFit: 'contain',
              display: 'block',
              margin: 0,
              padding: 0,
              lineHeight: 0,
              verticalAlign: 'top',
              cursor: 'default',
              opacity: SIDEBAR_THEME.logoOpacity,
              transition: 'opacity 0.2s ease, transform 0.2s ease',
            }}
          />
        </Box>
      </Box>
      
      {/* Logo quando contraído */}
      {!menuExpanded && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pt: '5px',
            pb: '6px',
            width: '100%',
            zIndex: 2,
          }}
        >
          <img
            src={BRAND_LOGO_MENU_COLLAPSED}
            alt="Logo"
            style={{
              height: 'auto',
              maxHeight: `${SIDEBAR_THEME.logoCollapsedMaxHeight}px`,
              width: 'auto',
              maxWidth: `${SIDEBAR_THEME.logoCollapsedMaxWidth}px`,
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto',
              transform: `translateX(${SIDEBAR_THEME.logoCollapsedTranslateX}px)`,
              cursor: 'default',
              opacity: SIDEBAR_THEME.logoOpacity,
            }}
          />
        </Box>
      )}
      
      <Divider
        sx={{
          flexShrink: 0,
          mx: menuExpanded ? 1.25 : 0.75,
          mb: 0.5,
          borderColor: SIDEBAR_THEME.borderSoft,
        }}
      />
      
      <List className="flex-1 w-full overflow-y-auto py-0 px-0 no-scrollbar" sx={{ 
        flex: 1,
        minHeight: 0,
        maxHeight: '100%',
        pt: 0.5,
        pb: 0.5,
        position: 'relative',
        zIndex: 2,
        overflowY: 'auto',
        overflowX: menuExpanded ? 'hidden' : 'visible',
      }}>
        {menuItems.map((item, index) => (
          <ListItem key={item.text} disablePadding sx={{ 
            width: '100%',
            display: 'block',
            position: 'relative'
          }}>
            {renderMenuItem(item, index)}
          </ListItem>
        ))}
      </List>
      
      {/* Perfil do usuário e botão de sair */}
      <Box sx={sidebarFooterSx}>
        <UnitSwitcher
          expanded={menuExpanded}
          onDropdownOpenChange={handleSidebarFooterDropdownOpenChange}
        />
        {authUser && (() => {
          const userAvatarSrc = resolveUserAvatarUrl(authUser);
          const openProfileDialog = () => {
            setProfileDialogOpen(true);
            if (isMobile) setMobileOpen(false);
          };
          const userRow = (
          <Box 
            sx={sidebarUserRowSx(menuExpanded)}
            onClick={openProfileDialog}
            role="button"
            aria-label="Abrir meu perfil"
          >
            <Avatar 
              src={userAvatarSrc}
              sx={{ 
                width: 34, 
                height: 34, 
                bgcolor: SIDEBAR_THEME.primary,
                fontSize: '0.8rem',
                fontWeight: 600,
                border: `2px solid ${SIDEBAR_THEME.primaryBorder}`,
                borderRadius: `${SIDEBAR_THEME.radiusSm}px`,
              }}
            >
              {!userAvatarSrc && (authUser.full_name?.charAt(0)?.toUpperCase() || 'U')}
            </Avatar>
            {menuExpanded && (
              <Box sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: SIDEBAR_THEME.navy, 
                    fontSize: '0.8rem',
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.3,
                  }}
                >
                  {authUser.full_name || 'Usuário'}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'inline-block',
                    px: 0.8,
                    py: 0.1,
                    borderRadius: '4px',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    bgcolor: ROLE_COLORS[authUser.role]?.bg || '#f0fdf4',
                    color: ROLE_COLORS[authUser.role]?.color || '#16a34a',
                    border: `1px solid ${ROLE_COLORS[authUser.role]?.border || '#bbf7d0'}`,
                    lineHeight: 1.4,
                  }}
                >
                  {getRoleLabel(authUser.role)}
                </Typography>
              </Box>
            )}
          </Box>
          );
          return menuExpanded ? userRow : (
            <Tooltip title="Meu perfil" placement="right">
              {userRow}
            </Tooltip>
          );
        })()}
        <Box
          sx={{
            px: menuExpanded ? 1.5 : 1,
            pt: 0.25,
            pb: 1,
            display: 'flex',
            flexDirection: menuExpanded ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: menuExpanded ? 'flex-start' : 'center',
            gap: menuExpanded ? 0.5 : 0.75,
            flexWrap: 'nowrap',
          }}
        >
          <LogoutButton variant="text" size="small" />
          {showCashRegisterShortcut && (
            menuExpanded ? (
              <Button
                variant="text"
                size="small"
                startIcon={<PointOfSaleIcon />}
                onClick={() => navigate(cashRegisterPath)}
                sx={{
                  color: isCashRegisterActive ? SIDEBAR_THEME.primary : SIDEBAR_THEME.menuText,
                  fontWeight: isCashRegisterActive ? 600 : 500,
                  fontSize: '0.78rem',
                  bgcolor: isCashRegisterActive ? SIDEBAR_THEME.primarySoft : 'transparent',
                  '&:hover': { bgcolor: SIDEBAR_THEME.primarySoft },
                }}
              >
                Caixa
              </Button>
            ) : (
              <Tooltip title="Caixa" placement="right">
                <IconButton
                  size="small"
                  onClick={() => navigate(cashRegisterPath)}
                  aria-label="Caixa"
                  sx={{
                    color: isCashRegisterActive ? SIDEBAR_THEME.primary : SIDEBAR_THEME.menuText,
                    bgcolor: isCashRegisterActive ? SIDEBAR_THEME.primarySoft : 'transparent',
                    '&:hover': { bgcolor: SIDEBAR_THEME.muted },
                  }}
                >
                  <PointOfSaleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )
          )}
          {showClinicSettingsShortcut && (
            <Tooltip title="Config. Clínica" placement={menuExpanded ? 'top' : 'right'}>
              <IconButton
                size="small"
                onClick={() => navigate(clinicSettingsPath)}
                aria-label="Config. Clínica"
                sx={{
                  color: isClinicSettingsActive ? SIDEBAR_THEME.primary : SIDEBAR_THEME.menuText,
                  bgcolor: isClinicSettingsActive ? SIDEBAR_THEME.primarySoft : 'transparent',
                  '&:hover': { bgcolor: SIDEBAR_THEME.muted },
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );

  const isInboxRoute = location.pathname.startsWith('/inbox');
  const isAssistantCentralRoute = location.pathname.startsWith('/central-ia');
  const isTeamChatRoute = location.pathname.startsWith('/team-chat');
  const isAgendaRoute = location.pathname === '/agenda';
  const isFinancialRoute = location.pathname.startsWith('/financial');
  const isCashRegisterRoute = location.pathname.startsWith('/financial/cash-register');
  const isTutoriaisRoute = location.pathname.startsWith('/tutoriais');
  const isPlatformCrmFormsRoute = location.pathname.startsWith('/platform-crm-forms');
  const isPlatformSupportRoute = location.pathname.startsWith('/platform-support');
  const isKanbanBoardRoute = /^\/kanban\/.+/.test(location.pathname);
  const isCommissionRulesRoute = location.pathname.startsWith('/financial/commission-rules');
  const isCrmRoute = location.pathname === '/crm' || location.pathname.startsWith('/crm/');
  /** Toolbar da própria página já traz o ≡ ou voltar (agenda, inbox, CRM, suporte…). */
  const mobilePageHasEmbeddedNavMenu =
    isInboxRoute
    || isPlatformSupportRoute
    || isAssistantCentralRoute
    || isAgendaRoute
    || isCrmRoute;
  const showMobileNavMenuBar = isMobile && !mobilePageHasEmbeddedNavMenu;
  /** Rotas cuja página controla o scroll internamente (grade full-height). */
  const isInternalScrollRoute =
    isInboxRoute ||
    isAssistantCentralRoute ||
    isTeamChatRoute ||
    isAgendaRoute ||
    isCashRegisterRoute ||
    isTutoriaisRoute ||
    isPlatformCrmFormsRoute ||
    isPlatformSupportRoute ||
    isKanbanBoardRoute ||
    isCommissionRulesRoute;

  /** Referência estável: abrir/fechar o menu não pode re-renderizar a página (Agenda, Inbox…). */
  const outletContext = useMemo(
    () => ({
      toggleDrawer: handleDrawerToggle,
      /** Barra global do MainLayout já exibe o ≡ — páginas não repetem no PageHeader. */
      mobileNavMenuInAppBar: showMobileNavMenuBar,
    }),
    [handleDrawerToggle, showMobileNavMenuBar],
  );

  const isTeamChatPath = location.pathname.startsWith('/team-chat');
  const isInboxPath = location.pathname.startsWith('/inbox');
  const floatingWidgets = useMemo(
    () => (isMobile ? null : (
      <>
        <TeamChatDock
          unreadTotal={teamChatUnread}
          onUnreadChange={setTeamChatUnread}
          visuallyHidden={isTeamChatPath}
          launcherBottomOffset={isInboxPath ? 76 : 16}
        />
        <VirtualAssistantWidget />
      </>
    )),
    [isMobile, teamChatUnread, isTeamChatPath, isInboxPath],
  );

  return (
    <Box
      className="app-main-layout-root"
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        height: '100%',
        width: '100%',
        maxHeight: isMobile ? 'none' : '100%',
        flexDirection: isMobile ? 'column' : 'row',
      }}
    >
      <CssBaseline />
      
      {/* Banner de notificação de pagamento */}
      <PaymentNotificationBanner />
      <MobileDesktopModeBanner show={desktopModeOnPhone} />
      <BrowserZoomOnboarding
        open={showZoomOnboarding}
        onClose={() => {
          markBrowserZoomOnboardingSeen(authUser?.id);
          clearFreshLoginZoomFlag();
          setShowZoomOnboarding(false);
        }}
      />
      <UserProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
      />
      
      <Box
        component="nav"
        sx={{
          width: isMobile ? 0 : `${desktopSidebarReservedWidth}px`,
          flexShrink: 0,
          overflow: isMobile ? 'hidden' : 'visible',
        }}
      >
        {/* Mobile Drawer */}
        {isMobile && (
        <SwipeableDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          onOpen={() => setMobileOpen(true)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: isMobile ? 'block' : 'none',
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: { xs: '280px', sm: drawerWidth },
              maxWidth: '85vw',
              height: '100%',
              overflow: 'hidden',
              backgroundColor: SIDEBAR_THEME.paper,
              borderRight: 'none',
              boxShadow: SIDEBAR_THEME.shadow,
              borderRadius: 0,
              top: 0,
              left: 0,
              position: 'relative',
              zIndex: theme.zIndex.drawer + 5,
            },
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            },
          }}
          swipeAreaWidth={20}
          disableSwipeToOpen={false}
        >
          {drawer}
        </SwipeableDrawer>
        )}

        {/* Desktop Drawer */}
        {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            display: isMobile ? 'none' : 'block',
            '& .MuiDrawer-paper': sidebarDesktopPaperSx(
              drawerOpen,
              bannerHeight,
              drawerMarginVertical,
              drawerMarginLeft,
              theme,
              drawerWidth,
              drawerWidthCollapsed,
              true,
            ),
          }}
          open
        >
          {drawer}
        </Drawer>
        )}
      </Box>
      
        <Box
        component="main"
        className="flex flex-col flex-1 relative"
        sx={{
          width: isMobile
            ? '100%'
            : `calc(100% - ${desktopSidebarReservedWidth + mainContentGap}px)`,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          pt: isMobile
            ? (isInboxRoute
              || isPlatformSupportRoute
              || isAssistantCentralRoute
              || isAgendaRoute
              || isCrmRoute
              ? bannerHeight > 0
                ? `${bannerHeight + (desktopModeOnPhone ? 56 : 0)}px`
                : desktopModeOnPhone
                  ? '56px'
                  : 0
              : showMobileNavMenuBar
                ? `${bannerHeight + (desktopModeOnPhone ? 56 : 0)}px`
                : `calc(env(safe-area-inset-top, 0px) + ${bannerHeight}px + ${desktopModeOnPhone ? 56 : 0}px)`)
            : `${bannerHeight + drawerMarginVertical}px`,
          position: 'relative',
          zIndex: 0,
          isolation: 'isolate',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          height: isMobile ? '100%' : 'var(--ui-viewport-height, 100dvh)',
          ...(isMobile
            ? {}
            : { minHeight: 'var(--ui-viewport-height, 100dvh)' }),
          overflow: 'hidden',
          bgcolor: isMobile ? '#ffffff' : undefined,
        }}
      >
        {showMobileNavMenuBar && (
          <Box
            component="header"
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              minHeight: 44,
              pt: 'max(4px, env(safe-area-inset-top, 0px))',
              pb: 0.25,
              px: 0.25,
              bgcolor: '#ffffff',
              borderBottom: `1px solid ${SIDEBAR_THEME.borderSoft}`,
              zIndex: 3,
            }}
          >
            <IconButton
              aria-label="Abrir menu lateral"
              onClick={handleDrawerToggle}
              size="medium"
              sx={{ color: SIDEBAR_THEME.navy }}
            >
              <MenuIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>
        )}

        <GlobalSystemFinder />

        {/* Régua de inadimplência: aviso antes do bloqueio, só para quem paga */}
        <PlatformBillingBanner state={platformBilling} canSee={userRole === 'admin_master'} />
        <PlatformContractGate />

        {/* Content area — layout edge-to-edge (mesmo padrão da Agenda) */}
        <Box
          className="flex-1"
          sx={{
            overflowY: isInternalScrollRoute ? 'hidden' : 'auto',
            overflowX: 'hidden',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <ImpersonationBanner />
          <Suspense fallback={<RouteLoadingFallback />}>
            <Outlet context={outletContext} />
          </Suspense>
        </Box>
      </Box>

      {/* Dialog de permissão de notificações */}
      {notificationSupported && (
        <NotificationPermissionDialog
          open={showNotificationDialog}
          onClose={handleCloseNotificationDialog}
          onPermissionGranted={handleNotificationPermissionGranted}
          onPermissionDenied={handleNotificationPermissionDenied}
        />
      )}

      {floatingWidgets}
    </Box>
  );
};

export default MainLayout;
